<script lang="ts" setup>
  import { computed, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select'
  import { useServices } from '~/api/models'
  import { useActiveSite } from '~/composables/useActiveSite'
  import { useSiteDelimitation } from '~/composables/useSiteDelimitation'
  import type { Site } from '~/types/locations'

  const NONE_VALUE = 'none'

  const { t } = useI18n()
  const authStore = useAuthStore()
  const services = useServices()
  const { switchSite, resetIfUnavailable } = useActiveSite()
  const showSiteDelimitation = useSiteDelimitation()
  // The active site selector is a debugging tool, hidden unless enabled in the runtime config.
  const isSelectorEnabled = String(useRuntimeConfig().public.siteSelectorEnabled) === 'true'

  const { data: sites } = services.locations.useGetAllSitesQuery()

  const hasNoSiteAccess = computed<boolean>(
    () => !!authStore.siteReach && !authStore.siteReach.unrestricted && authStore.siteReach.siteIds.length === 0
  )
  const isVisible = computed<boolean>(
    () => isSelectorEnabled && showSiteDelimitation.value && !!authStore.activeLicence
  )

  const selectedValue = computed<string>(() => (authStore.activeSite ? String(authStore.activeSite.id) : NONE_VALUE))

  const handleSiteChange = (value: unknown): void => {
    if (value === NONE_VALUE) {
      switchSite(undefined)
      return
    }

    switchSite((sites.value ?? []).find((site: Site) => String(site.id) === String(value)))
  }

  watch(
    [(): Site[] | undefined => sites.value, (): boolean => authStore.isSiteScopeEnabled],
    ([loadedSites, enabled]) => {
      if (enabled && loadedSites) {
        resetIfUnavailable(loadedSites)
      }
    },
    { immediate: true }
  )

  watch(
    (): boolean =>
      authStore.isSiteScopeEnabled && (!isSelectorEnabled || !showSiteDelimitation.value) && !!authStore.activeSite,
    (hiddenActiveSite) => {
      if (hiddenActiveSite) {
        switchSite(undefined)
      }
    },
    { immediate: true }
  )

  watch(
    (): boolean => authStore.siteReachLoaded && !authStore.isSiteScopeEnabled && !!authStore.activeSite,
    (staleActiveSite) => {
      if (staleActiveSite) {
        authStore.setActiveSite(undefined)
      }
    },
    { immediate: true }
  )
</script>

<template>
  <div v-if="isVisible" class="flex items-center gap-2 px-3 py-2 bg-white border-b">
    <span class="text-xs+ font-semibold text-content-title">{{ t('topbar.site.label') }}</span>
    <span v-if="hasNoSiteAccess" class="text-xs+ text-content-secondary" data-testid="no-site-access">
      {{ t('siteScope.noSiteAccess') }}
    </span>
    <Select v-else :model-value="selectedValue" @update:modelValue="handleSiteChange">
      <SelectTrigger class="h-8 w-56 bg-white">
        <SelectValue class="text-xs+" :placeholder="t('topbar.site.placeholder')" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem :value="NONE_VALUE">{{ t('topbar.site.none') }}</SelectItem>
          <SelectItem v-for="site in sites ?? []" :key="site.id" :value="String(site.id)">
            {{ site.name }}
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  </div>
</template>
